// src/Dashboard/pages/teacher/PrepaPhysique/utils/prepaPhysiqueUtils.js

import getWeightCategory from "../../../../../utils/getWeightCategory";

export const STOPPED_STATUSES = [
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié et Arrêt",
];

export const AGE_CATEGORIES = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
  "Inconnu",
];

export const seasonNeighbors = (season) => {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(season || ""));

  if (!match) {
    return { prev: "", next: "" };
  }

  const start = Number(match[1]);
  const end = Number(match[2]);

  return {
    prev: `${start - 1}-${end - 1}`,
    next: `${start + 1}-${end + 1}`,
  };
};

export const htmlToText = (html) => {
  if (!html) return "";

  if (typeof document === "undefined") {
    return String(html).replace(/<[^>]+>/g, " ").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

export const truncateText = (value, max = 160) => {
  const text = String(value || "").trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

export const formatDateFR = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
};

export const getAdherentName = (adherent) => {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

export const getGroupsLabel = (adherent) => {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
};

export const getBirthYear = (dateNaissance) => {
  if (!dateNaissance) return null;

  const date = new Date(dateNaissance);
  if (Number.isNaN(date.getTime())) return null;

  return date.getFullYear();
};

export const getAgeCategoryBySeason = (dateNaissance, season) => {
  if (!dateNaissance || !season) return "Inconnu";

  const birthYear = Number(getBirthYear(dateNaissance));
  const startYear = Number(String(season).split("-")?.[0]);

  if (!birthYear || !startYear) return "Inconnu";

  if (birthYear >= startYear - 5 && birthYear <= startYear - 3) {
    return "Eveil";
  }

  if (birthYear >= startYear - 7 && birthYear <= startYear - 6) {
    return "Mini-Poussin(e)";
  }

  if (birthYear >= startYear - 9 && birthYear <= startYear - 8) {
    return "Poussin(e)";
  }

  if (birthYear >= startYear - 11 && birthYear <= startYear - 10) {
    return "Benjamin(e)";
  }

  if (birthYear >= startYear - 13 && birthYear <= startYear - 12) {
    return "Minime";
  }

  if (birthYear >= startYear - 16 && birthYear <= startYear - 14) {
    return "Cadet(te)";
  }

  if (birthYear >= startYear - 19 && birthYear <= startYear - 17) {
    return "Junior";
  }

  if (birthYear >= startYear - 29 && birthYear <= startYear - 20) {
    return "Sénior";
  }

  if (birthYear <= startYear - 30) {
    return "Vétéran";
  }

  return "Inconnu";
};

export const normalizeAgeCategoryForWeight = (ageCategory) => {
  const value = String(ageCategory || "");

  const map = {
    Eveil: "Eveil",
    "Mini-Poussin(e)": "Mini-Poussin",
    "Mini-Poussin": "Mini-Poussin",
    "Poussin(e)": "Poussin",
    Poussin: "Poussin",
    "Benjamin(e)": "Benjamin",
    Benjamin: "Benjamin",
    Minime: "Minime",
    "Cadet(te)": "Cadet",
    Cadet: "Cadet",
    Junior: "Junior",
    Sénior: "Sénior",
    Senior: "Sénior",
    Vétéran: "Vétéran",
    Veteran: "Vétéran",
  };

  return map[value] || value;
};

export const getPhysicalCategories = (adherent, season) => {
  const ageCategory = getAgeCategoryBySeason(adherent?.date_naissance, season);
  const normalizedAgeCategory = normalizeAgeCategoryForWeight(ageCategory);

  const weightCategory = getWeightCategory(
    adherent?.genre,
    normalizedAgeCategory,
    adherent?.poids
  );

  return {
    ageCategory,
    normalizedAgeCategory,
    weightCategory,
    birthYear: getBirthYear(adherent?.date_naissance),
  };
};

export const uniqSorted = (values = []) => {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => {
    if (typeof a === "number" && typeof b === "number") {
      return a - b;
    }

    return String(a).localeCompare(String(b), "fr");
  });
};

export const getDistinctProgrammesFromExercises = (exercises = []) => {
  const programmes = uniqSorted(
    exercises
      .map((exercise) => exercise?.programme)
      .filter((value) => String(value || "").trim())
  );

  return ["All", ...programmes];
};

export const getDistinctProgrammesFromAssignments = (assignments = []) => {
  const programmes = uniqSorted(
    assignments
      .map((assignment) => assignment?.Exercice?.programme)
      .filter((value) => String(value || "").trim())
  );

  return ["All", ...programmes];
};

export const filterExercises = (exercises = [], filters = {}) => {
  const query = String(filters.search || "").trim().toLowerCase();

  return exercises
    .filter((exercise) => {
      const matchesSearch =
        !query ||
        [exercise?.nom, exercise?.programme, htmlToText(exercise?.description)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      const matchesProgramme =
        !filters.programme ||
        filters.programme === "All" ||
        exercise?.programme === filters.programme;

      return matchesSearch && matchesProgramme;
    })
    .sort((a, b) =>
      String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
    );
};

export const filterAssignments = (assignments = [], filters = {}) => {
  const query = String(filters.search || "").trim().toLowerCase();

  return assignments
    .filter((assignment) => {
      const adherentName = getAdherentName(assignment?.Adherent);
      const exerciseName = assignment?.Exercice?.nom || "";
      const programme = assignment?.Exercice?.programme || "";

      const matchesSearch =
        !query ||
        [adherentName, exerciseName, programme]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      const matchesProgramme =
        !filters.programme ||
        filters.programme === "All" ||
        programme === filters.programme;

      const done = !!assignment?.is_done;

      const matchesStatus =
        !filters.status ||
        filters.status === "All" ||
        (filters.status === "done" && done) ||
        (filters.status === "notdone" && !done);

      return matchesSearch && matchesProgramme && matchesStatus;
    })
    .sort((a, b) => {
      const nameA = getAdherentName(a?.Adherent);
      const nameB = getAdherentName(b?.Adherent);

      return nameA.localeCompare(nameB, "fr");
    });
};

export const calculateAssignmentStats = ({
  exercises = [],
  assignments = [],
  filteredAssignments = [],
  adherents = [],
}) => {
  const done = assignments.filter((item) => !!item?.is_done).length;
  const notDone = assignments.filter((item) => !item?.is_done).length;

  const distinctAdherents = new Set(
    assignments.map((item) => item?.adherent_id).filter(Boolean)
  );

  return {
    exercises: exercises.length,
    assignments: assignments.length,
    filteredAssignments: filteredAssignments.length,
    done,
    notDone,
    distinctAdherents: distinctAdherents.size,
    adherents: adherents.length,
  };
};